"use client"

import { useState, useEffect, useRef } from "react"
import type { Photo } from "@/lib/photos"
import { PhotoCard } from "@/components/photo-card"
import { ImageModal } from "@/components/image-modal"
import { Images } from "lucide-react"
import { getPhotoPolicy, getSelectionFavoriteToken, getSelectionShareToken, type SelectionPolicyMap } from "@/lib/selection-policy"
import { getLightboxPreviewUrl } from "@/lib/lightbox-image"

/** One desktop row; native lazy loading starts the rest well before they scroll into view. */
const EAGER_PHOTO_COUNT = 3

interface PhotoGridProps {
  photos: Photo[]
  ecommerceEnabled?: boolean
  favoritesEnabled?: boolean
  freeDownloadMaxWidth?: number
  downloadPolicy?: {
    freeDownloadsEnabled: boolean
    highResDownloadsEnabled: boolean
    highResDownloadMode: 'watermarked' | 'original'
    shareToken?: string
  }
  contentMode?: "sports-public" | "sports-private" | "travel-portfolio"
  /**
   * Server-resolved per-photo policies for grids that mix galleries (My Selection,
   * saved favorites). When set, a photo without a resolved policy can't be bought
   * or downloaded as an original.
   */
  photoPolicies?: SelectionPolicyMap
  watermarkSettings?: {
    opacity: number
    size: number
    position: string
    padding: number
    offsetX: number
    offsetY: number
    showBackground: boolean
    imagePath?: string
  }
}

export function PhotoGrid({ photos, ecommerceEnabled = true, favoritesEnabled = true, freeDownloadMaxWidth = 1200, downloadPolicy, photoPolicies, watermarkSettings, contentMode = "sports-public" }: PhotoGridProps) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(-1)
  const [visibleCount, setVisibleCount] = useState(12)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const selectedPhoto = selectedPhotoIndex >= 0 ? photos[selectedPhotoIndex] : null
  const visiblePhotos = photos.slice(0, visibleCount)
  const downloadVersion = [
    freeDownloadMaxWidth,
    watermarkSettings?.opacity ?? 'default',
    watermarkSettings?.size ?? 'default',
    watermarkSettings?.position ?? 'default',
    watermarkSettings?.padding ?? 'default',
    watermarkSettings?.offsetX ?? 'default',
    watermarkSettings?.offsetY ?? 'default',
    watermarkSettings?.showBackground ? 'bg' : 'nobg',
    watermarkSettings?.imagePath ?? 'default',
    downloadPolicy?.freeDownloadsEnabled === false ? 'nofree' : 'free',
    downloadPolicy?.highResDownloadsEnabled ? downloadPolicy.highResDownloadMode : 'nohighres',
    downloadPolicy?.shareToken ?? 'public',
  ].join('-')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + 12, photos.length))
        }
      },
      { rootMargin: "500px" }
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [photos.length])

  const getPhotoOptions = (photo: Photo) => {
    if (!photoPolicies) {
      return {
        ecommerceEnabled,
        freeDownloadsEnabled: downloadPolicy?.freeDownloadsEnabled !== false,
        highResDownloadsEnabled: downloadPolicy?.highResDownloadsEnabled === true,
        highResDownloadMode: downloadPolicy?.highResDownloadMode || 'original' as const,
        shareToken: downloadPolicy?.shareToken,
        favoriteToken: undefined,
      }
    }

    const policy = getPhotoPolicy(photoPolicies, photo)
    return {
      ecommerceEnabled: ecommerceEnabled && policy?.purchasable === true,
      freeDownloadsEnabled: policy ? policy.watermarkedDownloads : true,
      highResDownloadsEnabled: policy?.freeOriginal === true,
      highResDownloadMode: policy?.originalDownloadMode ?? 'original' as const,
      shareToken: getSelectionShareToken(photo) ?? downloadPolicy?.shareToken,
      favoriteToken: getSelectionFavoriteToken(photo),
    }
  }
  const selectedPhotoOptions = selectedPhoto ? getPhotoOptions(selectedPhoto) : null
  const getPreviewUrl = (photo: Photo | undefined) => {
    if (!photo) return undefined
    const options = getPhotoOptions(photo)
    return getLightboxPreviewUrl(photo, {
      freeDownloadsEnabled: options.freeDownloadsEnabled,
      highResDownloadsEnabled: options.highResDownloadsEnabled,
      highResDownloadMode: options.highResDownloadMode,
      freeDownloadMaxWidth,
      downloadVersion,
      shareToken: options.shareToken,
      favoriteToken: options.favoriteToken,
    })
  }
  const neighborPreviewUrls = selectedPhoto
    ? [getPreviewUrl(photos[selectedPhotoIndex + 1]), getPreviewUrl(photos[selectedPhotoIndex - 1])]
      .filter((url): url is string => Boolean(url))
    : []

  const handleNext = () => {
    if (selectedPhotoIndex < photos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1)
    }
  }

  const handlePrev = () => {
    if (selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1)
    }
  }

  if (photos.length === 0) {
    return (
      <div className="flex min-h-72 w-full flex-col items-center justify-center rounded-2xl border border-dashed bg-card p-8 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Images className="h-6 w-6" />
        </div>
        <h3 className="text-base font-semibold">No photos found</h3>
        <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
          Try a different search, or check back after more photos are added.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {visiblePhotos.map((photo, index) => {
          const options = getPhotoOptions(photo)
          return (
            <PhotoCard
              key={photo.id}
              photo={photo}
              priority={index < EAGER_PHOTO_COUNT}
              onClick={() => setSelectedPhotoIndex(index)}
              ecommerceEnabled={options.ecommerceEnabled}
              favoritesEnabled={favoritesEnabled}
              freeDownloadMaxWidth={freeDownloadMaxWidth}
              downloadVersion={downloadVersion}
              freeDownloadsEnabled={options.freeDownloadsEnabled}
              shareToken={options.shareToken}
              favoriteToken={options.favoriteToken}
            />
          )
        })}
      </div>

      {visibleCount < photos.length && (
        <div ref={loadMoreRef} className="h-20 w-full" />
      )}

      {selectedPhoto && selectedPhotoOptions && (
        <ImageModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhotoIndex(-1)}
          onNext={handleNext}
          onPrev={handlePrev}
          hasNext={selectedPhotoIndex < photos.length - 1}
          hasPrev={selectedPhotoIndex > 0}
          ecommerceEnabled={selectedPhotoOptions.ecommerceEnabled}
          favoritesEnabled={favoritesEnabled}
          freeDownloadMaxWidth={freeDownloadMaxWidth}
          downloadVersion={downloadVersion}
          freeDownloadsEnabled={selectedPhotoOptions.freeDownloadsEnabled}
          highResDownloadsEnabled={selectedPhotoOptions.highResDownloadsEnabled}
          highResDownloadMode={selectedPhotoOptions.highResDownloadMode}
          shareToken={selectedPhotoOptions.shareToken}
          favoriteToken={selectedPhotoOptions.favoriteToken}
          contentMode={contentMode}
          currentIndex={selectedPhotoIndex + 1}
          totalCount={photos.length}
          preloadUrls={neighborPreviewUrls}
        />
      )}
    </>
  )
}
